import React, { use, useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormLabel, FormControl, Select, MenuItem,
  InputLabel, Checkbox, Divider, Stepper, Step, StepLabel,
  Alert, Chip, Paper
} from "@mui/material";
import { useAuth } from "../../../../AuthContext";
import LoadingPage from "../../../Loader";
import { useNavigate } from "react-router-dom";
import ModuleSelection from "./ModuleSelection";
import { initiateDPOPayment, redirectToDPOPayment } from "./DPOPaymentService";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const countryCodes = [
  { code: "+264", label: "Namibia (+264)" },
  { code: "+27", label: "South Africa (+27)" },
  { code: "+1", label: "United States (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
];

// Bank details for EFT
const bankDetails = {
  bankName: "Bank Windhoek",
  accountName: "MINDSINACTION STEAM CENTRE PTY LTD",
  accountNumber: "8046582717",
  branchCode: "483-872",
  referenceFormat: ""
};

const Register = () => {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [registrationType, setRegistrationType] = useState('self');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    studentNumber: '',
    firstName: '',
    lastName: '',
    idNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    postalCode: '',
    phoneNumber: '',
    phoneCountryCode: countryCodes[0].code,
    selectedModules: [],
    parentFullName: '',
    parentPhone: '',
    parentPhoneCountryCode: countryCodes[0].code,
    parentRelationship: '',
    nextOfKinFullName: '',
    nextOfKinEmail: '',
    nextOfKinPhone: '',
    nextOfKinPhoneCountryCode: countryCodes[0].code,
    nextOfKinRelationship: '',
    paymentMethod: 'eft',
    acceptTerms: false,
    school: '',
    grade: '',
    proofOfPayment: null,
    proofOfPaymentName: '',
    category: 'primary'
  });
  const [errors, setErrors] = useState({});
  const { getData, addData, updateData } = useAuth();

  const generateToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const [userToken, setUserToken] = useState(() => generateToken())
  const navigate = useNavigate();

  let steps = [];

  if(registrationType === 'self'){
    steps = [
      'Account Type',
      'Student Details',
      'Next of Kin',
      'Modules Selection',
      'Payment'
    ];
  } else {
    steps = [
      'Account Type',
      'Parent Details',
      'Next of Kin',
      'Student Details',
      'Modules Selection',
      'Payment'
    ];
  }
  

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await getData('proccess.php', 'modules_V2');
        setModules(response.success 
          ? response.data.map(m => ({ 
              id: m.id, 
              name: m.module_name, 
              price: parseFloat(m.price),
              description: m.description,
              short_description: m.short_description,
              age_target_group: m.age_target_group,
              duration: m.duration,
              total_hours: m.total_hours,
              study_mode: m.study_mode,
              delivery_time: m.delivery_time
            })) 
          : []);

      } catch (error) {
        console.error("Module fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [getData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let sanitizedValue = value;
    if (name.includes('phoneNumber') || name.includes('parentPhone') || name.includes('nextOfKinPhone')) {
      sanitizedValue = value.replace(/[^0-9]/g, '');
    } else if (name === 'studentNumber' || name === 'idNumber') {
      sanitizedValue = value.replace(/[^0-9]/g, '');
    } else if (name === 'postalCode') {
      sanitizedValue = value.replace(/[^0-9]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitizedValue
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          proofOfPayment: 'File must be PDF, JPEG, JPG, or PNG'
        }));
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          proofOfPayment: 'File size must be less than 5MB'
        }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        proofOfPayment: file,
        proofOfPaymentName: file.name
      }));
      
      setErrors(prev => ({ ...prev, proofOfPayment: '' }));
    }
  };

  const handleModuleSelection = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      selectedModules: prev.selectedModules.includes(moduleId)
        ? prev.selectedModules.filter(id => id !== moduleId)
        : [...prev.selectedModules, moduleId]
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    let validators = {};

    if (registrationType === 'self') {
      validators = {
        0: () => {
          if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) 
            newErrors.email = 'Valid email is required';
          if (formData.password.length < 8) 
            newErrors.password = 'Password must be at least 8 characters';
          if (formData.password !== formData.confirmPassword) 
            newErrors.confirmPassword = 'Passwords do not match';
        },
        1: () => {
          
            if (!formData.firstName.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.firstName = 'Valid first name is required';
            if (!formData.lastName.match(/^[a-zA-Z]{2,}$/)) 
              newErrors.lastName = 'Valid last name is required';
            if (!formData.dateOfBirth) 
              newErrors.dateOfBirth = 'Date of birth is required';
            if (!formData.gender) 
              newErrors.gender = 'Gender is required';
            if (!formData.city.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.city = 'Valid city is required';
            if (!formData.school.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.school = 'School is required';
            if (!formData.grade.match(/^\d{1,2}$/)) 
              newErrors.grade = 'Grade is required';
        },
        2: () => {
          // ✅ ALWAYS validate next of kin
          if (!formData.nextOfKinFullName.match(/^[a-zA-Z\s]{2,}$/)) 
            newErrors.nextOfKinFullName = 'Valid full name is required';

          if (!formData.nextOfKinEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) 
            newErrors.nextOfKinEmail = 'Valid email is required';

          if (!formData.nextOfKinPhone.match(/^\d{7,}$/)) 
            newErrors.nextOfKinPhone = 'Valid phone number is required';

          if (!formData.nextOfKinRelationship) 
            newErrors.nextOfKinRelationship = 'Relationship is required';
        },
        3: () => {
          if (formData.selectedModules.length === 0) 
            newErrors.selectedModules = 'At least one module must be selected';
        },
        4: () => {
          if (!formData.acceptTerms) 
            newErrors.acceptTerms = 'You must accept the terms and conditions';
          
          // if (formData.paymentMethod === 'eft') {
          //   // if (!formData.proofOfPayment) 
          //   //   newErrors.proofOfPayment = 'Proof of payment is required for EFT';
          // }
        }
      };
    } else {
      validators = {
        0: () => {
          if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) 
            newErrors.email = 'Valid email is required';
          if (formData.password.length < 8) 
            newErrors.password = 'Password must be at least 8 characters';
          if (formData.password !== formData.confirmPassword) 
            newErrors.confirmPassword = 'Passwords do not match';
        },
        1: () => {
            if (!formData.parentFullName.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.parentFullName = 'Valid full name is required';
            if (!formData.parentPhone.match(/^\d{7,}$/)) 
              newErrors.parentPhone = 'Valid phone number is required';
            if (!formData.parentRelationship) 
              newErrors.parentRelationship = 'Relationship is required';
        },
        2: () => {
          // ✅ ALWAYS validate next of kin
          if (!formData.nextOfKinFullName.match(/^[a-zA-Z\s]{2,}$/)) 
            newErrors.nextOfKinFullName = 'Valid full name is required';

          if (!formData.nextOfKinEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) 
            newErrors.nextOfKinEmail = 'Valid email is required';

          if (!formData.nextOfKinPhone.match(/^\d{7,}$/)) 
            newErrors.nextOfKinPhone = 'Valid phone number is required';

          if (!formData.nextOfKinRelationship) 
            newErrors.nextOfKinRelationship = 'Relationship is required';
        },
        3: () => {
            if (!formData.firstName.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.firstName = 'Valid first name is required';
            if (!formData.lastName.match(/^[a-zA-Z]{2,}$/)) 
              newErrors.lastName = 'Valid last name is required';
            if (!formData.dateOfBirth) 
              newErrors.dateOfBirth = 'Date of birth is required';
            if (!formData.gender) 
              newErrors.gender = 'Gender is required';
            if (!formData.city.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.city = 'Valid city is required';
            if (!formData.school.match(/^[a-zA-Z\s]{2,}$/)) 
              newErrors.school = 'School is required';
            if (!formData.grade.match(/^\d{1,2}$/)) 
              newErrors.grade = 'Grade is required';
        },
        4: () => {
          if (formData.selectedModules.length === 0) 
            newErrors.selectedModules = 'At least one module must be selected';
        },
        5: () => {
          if (!formData.acceptTerms) 
            newErrors.acceptTerms = 'You must accept the terms and conditions';
          
          // if (formData.paymentMethod === 'eft') {
          //   // if (!formData.proofOfPayment) 
          //   //   newErrors.proofOfPayment = 'Proof of payment is required for EFT';
          // }
        }
      };
    }

    if (validators[step]) validators[step]();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);
      
      const course = formData.selectedModules.map(id => modules.find(m => m.id === id)?.id).filter(Boolean).join(', ');
      const physicalAddress = [formData.address, formData.city, formData.postalCode].filter(Boolean).join(', ');
      const age = formData.dateOfBirth ? Math.floor((new Date() - new Date(formData.dateOfBirth)) / 31557600000) : null;
      let parentId = null;
      let nextOfKinId = null;
      let studentId = null;

      // ✅ ALWAYS create next of kin
      const nextOfKinData = {
        fullName: formData.nextOfKinFullName,
        email: formData.nextOfKinEmail,
        cell: `${formData.nextOfKinPhoneCountryCode}${formData.nextOfKinPhone}`
      };

      const nextOfKinResponse = await addData('proccess.php', 'next_of_keen_V2', nextOfKinData);

      if (!nextOfKinResponse.success) {
        throw new Error(nextOfKinResponse.message || 'Next of kin registration failed');
      }

      const nextOfKinRecord = nextOfKinResponse.data.find(
        nk => nk.email === formData.nextOfKinEmail
      );

      if (!nextOfKinRecord) {
        throw new Error('Could not find matching next of kin record');
      }

      nextOfKinId = nextOfKinRecord.id;

      if (registrationType === 'self') {

        const studentData = {
          category: formData.category,
          registration_type: registrationType,
          next_of_keen_id: nextOfKinId,
          parent_relationship: formData.nextOfKinRelationship,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          studentToken: `${userToken}`,
          age: age,
          school: formData.school,
          grade: formData.grade,
          physicalAddress: physicalAddress,
          status: 0,
          email: formData.email,
          cell: `${formData.phoneCountryCode}${formData.phoneNumber}`,
        };

        
        const studentResponse = await addData('proccess.php', 'students_V2', studentData);
        
        if (!studentResponse.success) {
          throw new Error(studentResponse.message || 'Student registration failed');
        }

        const studentRecord = studentResponse.data.find(s => s.studentToken === userToken);
        if (!studentRecord) {
          throw new Error('Could not find matching student record');
        }
        studentId = studentRecord.id;
      } else {
        const parentData = {
          fullName: formData.parentFullName,
          email: formData.email,
          password: formData.password,
          parentToken: `${userToken}`,
          cell: `${formData.parentPhoneCountryCode}${formData.parentPhone}`,
          status: 0,
        };

        console.log(userToken);
        
        
        const parentResponse = await addData('proccess.php', 'parent_V2', parentData);
        
        if (!parentResponse.success) {
          throw new Error(parentResponse.message || 'Parent registration failed');
        }

        const parentRecord = parentResponse.data.find(p => p.email === formData.email);
        if (!parentRecord) {
          throw new Error('Could not find matching parent record');
        }
        parentId = parentRecord.id;

        const studentData = {
          category: formData.category,
          registration_type: registrationType,
          next_of_keen_id: nextOfKinId,
          parent_id: parentId,
          parent_relationship: formData.parentRelationship,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          studentToken: `${userToken}`,
          age: age,
          school: formData.school,
          grade: formData.grade,
          physicalAddress: physicalAddress,
          status: 0,
          email: formData.email,
          cell: `${formData.phoneCountryCode}${formData.phoneNumber}`
        };
        
        const studentResponse = await addData('proccess.php', 'students_V2', studentData);
        
        if (!studentResponse.success) {
          throw new Error(studentResponse.message || 'Student registration failed');
        }

        const studentRecord = studentResponse.data.find(s => s.studentToken === userToken);
        if (!studentRecord) {
          throw new Error('Could not find matching student record');
        }
        studentId = studentRecord.id;
      }

      // Handle payment
      const paymentData = {
        student_Id: studentId,
        payToken: `${userToken}`,
        paymentMethod: formData.paymentMethod,
        payment_reference: `STU${studentId}PAY${userToken}`,
        trans_Ref: '',
        trans_status: '000',
        trans_token: '',
        trans_result_explanation: '',
        dueToUs: calculateTotal(),
        dueToYou: 0,
        paid_amount: 0,
        image: formData.proofOfPayment ? formData.proofOfPayment : null
      }

      const paymentResponse = await addData('proccess.php', 'payment_V2', paymentData);

      if (!paymentResponse.success) {
        throw new Error(paymentResponse.message || 'Payment record creation failed');
      }

      const paymentRecord = paymentResponse.data.find(p => p.payToken === userToken && p.student_Id === studentId);
      if (!paymentRecord) {
        throw new Error('Could not find matching payment record');
      }

      const paymentId = paymentRecord.id;

      // Handle registration
      const registrationData = {
        student_Id: studentId,
        module_Id: course,
        payment_Id: paymentId,
      }

      const registrationResponse = await addData('proccess.php', 'registration_V2', registrationData);
      if (!registrationResponse.success) {
        throw new Error(registrationResponse.message || 'Registration record creation failed');
      }


      if (formData.paymentMethod === 'DPO') {
        const paymentData = {
          amount: calculateTotal(),
          currency: 'NAD',
          customerEmail: formData.email,
          customerFirstName: registrationType === 'self' ? formData.firstName : formData.parentFullName.split(' ')[0],
          customerLastName: registrationType === 'self' ? formData.lastName : formData.parentFullName.split(' ').slice(1).join(' '),
          customerPhone: registrationType === 'self' ? `${formData.phoneCountryCode}${formData.phoneNumber}` : `${formData.parentPhoneCountryCode}${formData.parentPhone}`,
          selectedModules: formData.selectedModules.map(id => 
            modules.find(m => m.id === id)?.name).filter(Boolean),
          serviceType: '3854',
          serviceDescription: 'Course Registration',
          redirectURL: `${window.location.origin}/payment/success`,
          backURL: `${window.location.origin}/payment/cancel`
        };

        const paymentResult = await initiateDPOPayment(paymentData);
        if (!paymentResult.success) {
          throw new Error(paymentResult.message || 'Payment initiation failed');
        }

        const updatePaymentData = {
          paid_amount: calculateTotal(),
          payment_reference: `STU${studentId}PAY${userToken}`,
          trans_token: paymentResult.token,
          trans_Ref: paymentResult.transectionRef,
          trans_status: '000',
          trans_result_explanation: 'Transaction created'
        };

        const updateResponse = await updateData(
          'proccess.php',
          'payment_V2',
          updatePaymentData,
          paymentId
        );

        if (!updateResponse.success) {
          throw new Error(updateResponse.message || 'Failed to update payment transaction details');
        }

        redirectToDPOPayment(paymentResult.paymentUrl);
      } else if (formData.paymentMethod === 'eft') {
        
        const formDataToSend = new FormData();
        formDataToSend.append('studentId', studentId);
        formDataToSend.append('paymentMethod', 'eft');
        formDataToSend.append('amount', calculateTotal());
        formDataToSend.append('reference', `STU${studentId}PAY${userToken}`);
        
        if (formData.proofOfPayment) {
          formDataToSend.append('proofOfPayment', formData.proofOfPayment);
        }

        const updatePaymentData = {
          trans_status: '000',
          trans_result_explanation: 'EFT payment - Proof uploaded, awaiting verification',
          paymentMethod: 'eft',
          payment_reference: `STU${studentId}PAY${userToken}`
        };

        const updateResponse = await updateData(
          'proccess.php',
          'payment_V2',
          updatePaymentData,
          paymentId
        );

        if (!updateResponse.success) {
          throw new Error(updateResponse.message || 'Failed to update payment details');
        }

        navigate('/registration/success', {
          state: {
            message: 'Registration successful! Your account is pending payment verification.',
            studentNumber: studentId,
            reference: `STU${studentId}PAY${userToken}`,
            amount: calculateTotal(),
            bankDetails: bankDetails,
            email: formData.email,
            course: formData.selectedModules,
            fullname: `${formData.firstName} ${formData.lastName}`,
            paymentMethod: formData.paymentMethod

          }
        });
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      alert(`Registration error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return formData.selectedModules.reduce((total, moduleId) => {
      const module = modules.find(m => m.id === moduleId);
      return total + (module?.price || 0);
    }, 0);
  };

  const removeProofOfPayment = () => {
    setFormData(prev => ({
      ...prev,
      proofOfPayment: null,
      proofOfPaymentName: ''
    }));
  };

  if (loading) return <LoadingPage />;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: 3,
      }}
    >
      <Box sx={{height: '10vh'}}></Box>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          marginTop: '45px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
          p: 4,
          animation: Object.keys(errors).length > 0
            ? 'flashError 0.9s ease-in-out 3'
            : 'none',
          '@keyframes flashError': {
            '0%, 33%, 66%, 100%': { backgroundColor: 'background.paper' },
            '16.5%, 49.5%': { backgroundColor: '#f44336' },
          },
        }}
      >
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4 }}>
          Student Account Registration
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {registrationType === 'self' ? (
          <>
            {activeStep === 0 && (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">I am registering:</FormLabel>
                  <RadioGroup
                    name="registrationType"
                    value={registrationType}
                    onChange={(e) => setRegistrationType(e.target.value)}
                  >
                    <FormControlLabel value="self" control={<Radio />} label="For myself as a student" />
                    <FormControlLabel value="parent" control={<Radio />} label="On behalf of my child" />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email}
                  inputProps={{ pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$" }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password}
                  inputProps={{ minLength: 8 }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  inputProps={{ minLength: 8 }}
                />
              </Box>
            )}

            {activeStep === 1 && (registrationType === 'self' ? (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>Student Details</Typography>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  inputProps={{ pattern: "[a-zA-Z]{2,}" }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  inputProps={{ pattern: "[a-zA-Z]{2,}" }}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    label="Category"
                    onChange={handleChange}
                  >
                    <MenuItem value="primary">Primary School Learner</MenuItem>
                    <MenuItem value="secondary">Secondary School Learner</MenuItem>
                    <MenuItem value="tertiary">Tertiary Student</MenuItem>
                    <MenuItem value="outOfSchool">Out Of School Youth</MenuItem>
                    <MenuItem value="hobbyist">Hobbyists</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="ID Number(optional)"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.idNumber}
                  helperText={errors.idNumber}
                  inputProps={{ maxLength: 20 }}
                />
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth margin="normal" error={!!errors.gender}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    label="Gender"
                    onChange={handleChange}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {errors.gender && (
                    <Typography color="error" variant="caption">
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="physical Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.address}
                  helperText={errors.address}
                />
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.city}
                  helperText={errors.city}
                />
                <TextField
                  fullWidth
                  label="School"
                  name="school"
                  value={formData.school}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.school}
                  helperText={errors.school}
                />
                <TextField
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.grade}
                  helperText={errors.grade}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="phoneCountryCode"
                      value={formData.phoneCountryCode}
                      label="Country Code"
                      onChange={handleChange}
                    >
                      {countryCodes.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    inputProps={{ pattern: "[0-9]{7,}", maxLength: 15 }}
                  />
                </Box>
              </Box>
            ) : null)}

            {activeStep === 2 && (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>Next of Kin Details</Typography>

                <TextField
                  fullWidth
                  label="Full Name"
                  name="nextOfKinFullName"
                  value={formData.nextOfKinFullName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.nextOfKinFullName}
                  helperText={errors.nextOfKinFullName}
                />

                <TextField
                  fullWidth
                  label="Email"
                  name="nextOfKinEmail"
                  type="email"
                  value={formData.nextOfKinEmail}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.nextOfKinEmail}
                  helperText={errors.nextOfKinEmail}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="nextOfKinPhoneCountryCode"
                      value={formData.nextOfKinPhoneCountryCode}
                      label="Country Code"
                      onChange={handleChange}
                    >
                      {countryCodes.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="nextOfKinPhone"
                    value={formData.nextOfKinPhone}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.nextOfKinPhone}
                    helperText={errors.nextOfKinPhone}
                  />
                </Box>

                <FormControl fullWidth margin="normal" error={!!errors.nextOfKinRelationship}>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    name="nextOfKinRelationship"
                    value={formData.nextOfKinRelationship}
                    label="Relationship"
                    onChange={handleChange}
                  >
                    <MenuItem value="father">Father</MenuItem>
                    <MenuItem value="mother">Mother</MenuItem>
                    <MenuItem value="guardian">Guardian</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {activeStep === 3 && (
              <ModuleSelection
                modules={modules}
                selectedModules={formData.selectedModules}
                onModuleSelection={handleModuleSelection}
                errors={errors}
                calculateTotal={calculateTotal}
              />
            )}

            {activeStep === 4 && (
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                <Typography variant="h6" gutterBottom>Payment Information</Typography>
                
                <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Payment Summary</Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Selected Modules:</Typography>
                    <Typography>{formData.selectedModules.length}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Total Amount:</Typography>
                    <Typography>N${calculateTotal().toFixed(2)}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography>Amount Due:</Typography>
                    <Typography>N${calculateTotal().toFixed(2)}</Typography>
                  </Box>
                </Box>

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">Payment Method</FormLabel>
                  <RadioGroup
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="eft"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>Electronic Funds Transfer (EFT)</Typography>
                          <Chip 
                            label="Available" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="DPO"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.5 }}>
                          <img
                            src="https://s3-us-west-2.amazonaws.com/cbi-image-service-prd/modified/09836cd2-75e1-4a15-8160-e98a4b12c9b2.png"
                            alt="DPO"
                            style={{ height: 20, marginLeft: 8 }}
                          />
                          <Chip 
                            label="Temporarily Unavailable" 
                            color="default" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                      disabled
                    />
                  </RadioGroup>
                </FormControl>

                

                {formData.paymentMethod === 'eft' && (
                  <>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom display={'flex'} gap={'1rem'} alignItems={'center'}>
                          Upload Proof of Payment <Typography fontWeight={700} color="red">(OPTIONAL)</Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Please upload a scanned copy or screenshot of your bank transfer confirmation.
                          Accepted formats: PDF, JPG, JPEG, PNG (Max 5MB)
                        </Typography>
                        
                        {!formData.proofOfPayment ? (
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            sx={{ mt: 1 }}
                          >
                            Upload Proof of Payment
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleFileUpload}
                            />
                          </Button>
                        ) : (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mt: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AttachFileIcon sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {formData.proofOfPaymentName}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              onClick={removeProofOfPayment}
                            >
                              Remove
                            </Button>
                          </Paper>
                        )}
                        {errors.proofOfPayment && (
                          <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                            {errors.proofOfPayment}
                          </Typography>
                        )}
                      </Box>
                    </Alert>

                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography>Bank Accout Details:</Typography>
                        <ul>
                          <li>Bank Name: <Typography color="orangered" fontWeight={700}>{bankDetails.bankName}</Typography> </li>
                          <li>Account Name: <Typography color="orangered" fontWeight={700}>{bankDetails.accountName}</Typography> </li>
                          <li>Account Number: <Typography color="orangered" fontWeight={700}>{bankDetails.accountNumber}</Typography> </li>
                          <li>Bank Branch Code: <Typography color="orangered" fontWeight={700}>{bankDetails.branchCode}</Typography> </li>
                        </ul>
                      </Box>
                    </Alert>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        <strong>Important:</strong> Your registration will be pending until we verify your payment.
                      </Typography>
                    </Alert>
                  </>
                )}

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="I agree to the terms and conditions"
                  />
                  {errors.acceptTerms && (
                    <Typography color="error" variant="caption">
                      {errors.acceptTerms}
                    </Typography>
                  )}
                </Box>

                {formData.paymentMethod === 'eft' ? (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    By clicking "Complete Registration", you will submit your registration and payment proof for verification.
                    You will receive an email confirmation once your payment is verified.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    DPO payment is temporarily unavailable. Please select EFT payment method.
                  </Typography>
                )}
              </Box>
            )}
          </>
        ):(
          <>
            {activeStep === 0 && (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">I am registering:</FormLabel>
                  <RadioGroup
                    name="registrationType"
                    value={registrationType}
                    onChange={(e) => setRegistrationType(e.target.value)}
                  >
                    <FormControlLabel value="self" control={<Radio />} label="For myself as a student" />
                    <FormControlLabel value="parent" control={<Radio />} label="On behalf of my child" />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email}
                  inputProps={{ pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$" }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password}
                  inputProps={{ minLength: 8 }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  inputProps={{ minLength: 8 }}
                />
              </Box>
            )}

            {activeStep === 1 && (registrationType === 'self' ? (
              null
            ) : (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>Parent Details</Typography>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="parentFullName"
                  value={formData.parentFullName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.parentFullName}
                  helperText={errors.parentFullName}
                  inputProps={{ pattern: "[a-zA-Z\\s]{2,}" }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="parentPhoneCountryCode"
                      value={formData.parentPhoneCountryCode}
                      label="Country Code"
                      onChange={handleChange}
                    >
                      {countryCodes.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="parentPhone"
                    type="tel"
                    value={formData.parentPhone}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.parentPhone}
                    helperText={errors.parentPhone}
                    inputProps={{ pattern: "[0-9]{7,}", maxLength: 15 }}
                  />
                </Box>
                <FormControl fullWidth margin="normal" error={!!errors.parentRelationship}>
                  <InputLabel>Relationship to Student</InputLabel>
                  <Select
                    name="parentRelationship"
                    value={formData.parentRelationship}
                    label="Relationship to Student"
                    onChange={handleChange}
                  >
                    <MenuItem value="father">Father</MenuItem>
                    <MenuItem value="mother">Mother</MenuItem>
                    <MenuItem value="guardian">Guardian</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {errors.parentRelationship && (
                    <Typography color="error" variant="caption">
                      {errors.parentRelationship}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            ))}

            

            {activeStep === 2 && (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>Next of Kin Details</Typography>

                <TextField
                  fullWidth
                  label="Full Name"
                  name="nextOfKinFullName"
                  value={formData.nextOfKinFullName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.nextOfKinFullName}
                  helperText={errors.nextOfKinFullName}
                />

                <TextField
                  fullWidth
                  label="Email"
                  name="nextOfKinEmail"
                  type="email"
                  value={formData.nextOfKinEmail}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.nextOfKinEmail}
                  helperText={errors.nextOfKinEmail}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="nextOfKinPhoneCountryCode"
                      value={formData.nextOfKinPhoneCountryCode}
                      label="Country Code"
                      onChange={handleChange}
                    >
                      {countryCodes.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="nextOfKinPhone"
                    value={formData.nextOfKinPhone}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.nextOfKinPhone}
                    helperText={errors.nextOfKinPhone}
                  />
                </Box>

                <FormControl fullWidth margin="normal" error={!!errors.nextOfKinRelationship}>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    name="nextOfKinRelationship"
                    value={formData.nextOfKinRelationship}
                    label="Relationship"
                    onChange={handleChange}
                  >
                    <MenuItem value="father">Father</MenuItem>
                    <MenuItem value="mother">Mother</MenuItem>
                    <MenuItem value="guardian">Guardian</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {activeStep === 3 && (registrationType !== 'self' ? (
              <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>Student Details</Typography>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  inputProps={{ pattern: "[a-zA-Z]{2,}" }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  inputProps={{ pattern: "[a-zA-Z]{2,}" }}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    label="Category"
                    onChange={handleChange}
                  >
                    <MenuItem value="primary">Primary School Learner</MenuItem>
                    <MenuItem value="secondary">Secondary School Learner</MenuItem>
                    <MenuItem value="tertiary">Tertiary Student</MenuItem>
                    <MenuItem value="outOfSchool">Out Of School Youth</MenuItem>
                    <MenuItem value="hobbyist">Hobbyists</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="ID Number(optional)"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.idNumber}
                  helperText={errors.idNumber}
                  inputProps={{ maxLength: 20 }}
                />
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth margin="normal" error={!!errors.gender}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    label="Gender"
                    onChange={handleChange}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {errors.gender && (
                    <Typography color="error" variant="caption">
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="physical Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.address}
                  helperText={errors.address}
                />
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.city}
                  helperText={errors.city}
                />
                <TextField
                  fullWidth
                  label="School"
                  name="school"
                  value={formData.school}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.school}
                  helperText={errors.school}
                />
                <TextField
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.grade}
                  helperText={errors.grade}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="phoneCountryCode"
                      value={formData.phoneCountryCode}
                      label="Country Code"
                      onChange={handleChange}
                    >
                      {countryCodes.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    inputProps={{ pattern: "[0-9]{7,}", maxLength: 15 }}
                  />
                </Box>
              </Box>
            ) : (
              null
            ))}

            {activeStep === 4 && (
              <ModuleSelection
                modules={modules}
                selectedModules={formData.selectedModules}
                onModuleSelection={handleModuleSelection}
                errors={errors}
                calculateTotal={calculateTotal}
              />
            )}

            {activeStep === 5 && (
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                <Typography variant="h6" gutterBottom>Payment Information</Typography>
                
                <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Payment Summary</Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Selected Modules:</Typography>
                    <Typography>{formData.selectedModules.length}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Total Amount:</Typography>
                    <Typography>N${calculateTotal().toFixed(2)}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography>Amount Due:</Typography>
                    <Typography>N${calculateTotal().toFixed(2)}</Typography>
                  </Box>
                </Box>

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">Payment Method</FormLabel>
                  <RadioGroup
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="eft"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography>Electronic Funds Transfer (EFT)</Typography>
                          <Chip 
                            label="Available" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="DPO"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.5 }}>
                          <img
                            src="https://s3-us-west-2.amazonaws.com/cbi-image-service-prd/modified/09836cd2-75e1-4a15-8160-e98a4b12c9b2.png"
                            alt="DPO"
                            style={{ height: 20, marginLeft: 8 }}
                          />
                          <Chip 
                            label="Temporarily Unavailable" 
                            color="default" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                      disabled
                    />
                  </RadioGroup>
                </FormControl>

                

                {formData.paymentMethod === 'eft' && (
                  <>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom display={'flex'} gap={'1rem'} alignItems={'center'}>
                          Upload Proof of Payment <Typography fontWeight={700} color="red">(OPTIONAL)</Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Please upload a scanned copy or screenshot of your bank transfer confirmation.
                          Accepted formats: PDF, JPG, JPEG, PNG (Max 5MB)
                        </Typography>
                        
                        {!formData.proofOfPayment ? (
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            sx={{ mt: 1 }}
                          >
                            Upload Proof of Payment
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleFileUpload}
                            />
                          </Button>
                        ) : (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mt: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AttachFileIcon sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {formData.proofOfPaymentName}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              onClick={removeProofOfPayment}
                            >
                              Remove
                            </Button>
                          </Paper>
                        )}
                        {errors.proofOfPayment && (
                          <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                            {errors.proofOfPayment}
                          </Typography>
                        )}
                      </Box>
                    </Alert>

                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography>Bank Accout Details:</Typography>
                        <ul>
                          <li>Bank Name: <Typography color="orangered" fontWeight={700}>{bankDetails.bankName}</Typography> </li>
                          <li>Account Name: <Typography color="orangered" fontWeight={700}>{bankDetails.accountName}</Typography> </li>
                          <li>Account Number: <Typography color="orangered" fontWeight={700}>{bankDetails.accountNumber}</Typography> </li>
                          <li>Bank Branch Code: <Typography color="orangered" fontWeight={700}>{bankDetails.branchCode}</Typography> </li>
                        </ul>
                      </Box>
                    </Alert>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        <strong>Important:</strong> Your registration will be incomplete until we verify your payment.
                      </Typography>
                    </Alert>
                  </>
                )}

                <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography>Terms and Conditions:</Typography>
                        <ul>
                          <li><Typography color="orange" fontWeight={700}>Full payment is required prior to participation in the program.</Typography> </li>
                          <li><Typography color="orange" fontWeight={700}>It is compulsory to share a proof of payment, and a copy of the applicant's full birth certificate or ID.</Typography> </li>
                          <li><Typography color="orange" fontWeight={700}>Mindsinaction STEAM Centre reserves the right to adjust the course format and schedule.</Typography> </li>
                        </ul>
                      </Box>
                    </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="I agree to the terms and conditions"
                  />
                  {errors.acceptTerms && (
                    <Typography color="error" variant="caption">
                      {errors.acceptTerms}
                    </Typography>
                  )}
                </Box>

                {formData.paymentMethod === 'eft' ? (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    By clicking "Complete Registration", you will submit your registration and payment proof for verification.
                    You will receive an email confirmation once your payment is verified.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    DPO payment is temporarily unavailable. Please select EFT payment method.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.acceptTerms}
            >
              Complete Registration
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Register;